import { useNewCrremAsLegacyRows } from "features/crrem-pathway-chart/hooks/useNewCrremAsLegacyRows";
import { parseBuildingAreas } from "features/crrem-pathway-chart/lib/parseBuildingAreas";
import { scaleChartValue } from "features/crrem-pathway-chart/lib/scaleChartValues";
import { createContext, ReactNode, useEffect, useMemo, useState } from "react";

import {
  useGetAllMeasures,
  useGetAllSelectedMeasures,
  useGetBuilding,
  useGetBuildingEPCInfo,
  useGetCrrem,
} from "../api/hooks/allHooks";
import { MODERNIZATION_MAX_YEAR, MODERNIZATION_MIN_YEAR, RETROFITS_MIN_YEAR } from "../configs/Modernization";
import { columnsAPINamesEnums } from "../enums/Modernization/financialDashboard";
import { addRetrofitsToData } from "../functions/Modernization";
import { isNumeric, isNumericGuard } from "../utils/dataManipulation";
import { getBGFToNGFRatio } from "../utils/general";
import { convertValueBGFToNGF } from "../utils/Modernization";

export type Section = "crrem" | "energy";

type ContextValue = {
  crremData: CrremRow[];
  crremScenario: string | undefined;
  data: DataRow[];
  dataEmission: DataRow[];
  dataYearRange: [number, number];
  epcInfo: any;
  epcIsLoading: boolean;
  isAllmeasuresLoading: boolean;
  isSelectedMesuresLoading: boolean;
  measures: Measure[];
  quantity: "consumption" | "emission";
  section: Section;
  selectedMeaures: SelectedMeasure[];
  setCrremScenario: (newData: ContextValue["crremScenario"]) => void;
  setData: (newData: ContextValue["data"]) => void;
  setQuantity: (newQuantity: ContextValue["quantity"]) => void;
  setSection: (newQuantity: ContextValue["section"]) => void;
  setYearRange: (yearRange: ContextValue["yearRange"]) => void;
  yearRange: [number, number];
};

type CrremRow = Exclude<DataRow, "modernized">;

type DataRow = {
  baseline?: number | undefined;
  climate: number | undefined;
  crrem: number | undefined;
  current: number | undefined;
  id: number;
  modernized?: number | undefined;
  project: number;
  year: number | undefined;
};

type Measure = Record<(typeof columnsAPINamesEnums)[keyof typeof columnsAPINamesEnums], number | string | undefined>;

type SelectedMeasure = {
  id: number;
  name: string;
  project: number;
  retrofit_model: number;
  user: number;
  year: number;
};

const defaultValue: ContextValue = {
  crremData: [],
  crremScenario: undefined,
  data: [],
  dataEmission: [],
  dataYearRange: [MODERNIZATION_MIN_YEAR, MODERNIZATION_MAX_YEAR],
  epcInfo: {},
  epcIsLoading: false,
  isAllmeasuresLoading: false,
  isSelectedMesuresLoading: false,
  measures: [],
  quantity: "emission",
  section: "crrem",
  selectedMeaures: [],
  setCrremScenario: () => {},
  setData: () => {},
  setQuantity: () => "emission",
  setSection: () => {},
  setYearRange: () => {},
  yearRange: [MODERNIZATION_MIN_YEAR, MODERNIZATION_MAX_YEAR],
};

export const WizardContext = createContext<ContextValue>(defaultValue);

const getPrimaryEnergy = (epcInfoAll: any, crremDataRaw: any, row: any) => {
  let factor = 1;
  if (isNumeric(epcInfoAll?.primary_energy_kwh_per_m2)) {
    factor = Number(epcInfoAll?.primary_energy_kwh_per_m2) / crremDataRaw?.[0]?.climate;
  }
  return row.climate * factor;
};

export const WizardContextProvider = ({
  children,
  defaultQuantity = "emission",
  hasModernization = true,
  projectId,
}: {
  children: ReactNode;
  defaultQuantity?: ContextValue["quantity"];
  hasModernization?: boolean;
  projectId: number | string;
}) => {
  const [data, setData] = useState<ContextValue["data"]>(defaultValue.data);
  const [dataEmission, setDataEmission] = useState<ContextValue["dataEmission"]>(defaultValue.data);
  const [quantity, setQuantity] = useState<ContextValue["quantity"]>(defaultQuantity);
  const [section, setSection] = useState<ContextValue["section"]>(defaultValue.section);
  const [yearRange, setYearRange] = useState<ContextValue["yearRange"]>(defaultValue.yearRange);

  const { data: selectedMeaures, isLoading: isSelectedMesuresLoading } = useGetAllSelectedMeasures(
    projectId,
    hasModernization
  );

  const { data: measures, isLoading: isAllmeasuresLoading } = useGetAllMeasures(projectId, hasModernization);

  const [retrofits, setRetrofits] = useState(selectedMeaures);

  const [crremScenario, setCrremScenario] = useState<ContextValue["crremScenario"]>();

  const newCrremRows = useNewCrremAsLegacyRows(projectId, quantity);
  const { data: crremDataRawLegacy } = useGetCrrem(
    projectId,
    crremScenario,
    quantity,
    !newCrremRows.isLoading && !newCrremRows.hasData
  );
  const crremDataRaw = newCrremRows.hasData
    ? quantity === "consumption"
      ? newCrremRows.consumption
      : newCrremRows.emission
    : crremDataRawLegacy;

  const { data: building } = useGetBuilding(projectId);
  const { data: epcInfoInitial, isLoading: epcIsLoading } = useGetBuildingEPCInfo(projectId);
  const epcInfo = useMemo(() => (epcInfoInitial?.length ? epcInfoInitial[0] : {}), [epcInfoInitial]);
  const BGF_NGF_Ratio = getBGFToNGFRatio(building ?? {});
  const newSeriesDivisor = parseBuildingAreas(building).grossArea;
  const crremDataForContext = useMemo(() => {
    if (!newCrremRows.hasData || !newSeriesDivisor) return crremDataRaw;
    return crremDataRaw?.map((row: CrremRow) => ({
      ...row,
      climate: row.climate === undefined ? undefined : scaleChartValue(row.climate, newSeriesDivisor),
      current: row.current === undefined ? undefined : scaleChartValue(row.current, newSeriesDivisor),
    }));
  }, [crremDataRaw, newCrremRows.hasData, newSeriesDivisor]);

  const crremData = useMemo(
    () =>
      crremDataForContext?.map((row: CrremRow) => ({
        ...row,
        baseline: row.current,
        baselinePrimary: getPrimaryEnergy(epcInfo, crremDataForContext, row),
        current: row.climate,
      })) ?? [],
    [crremDataForContext, epcInfo]
  );
  const newEmissionCrremData = useMemo(() => {
    const scaled = newSeriesDivisor
      ? newCrremRows.emission?.map((row: CrremRow) => ({
          ...row,
          climate: row.climate === undefined ? undefined : scaleChartValue(row.climate, newSeriesDivisor),
          current: row.current === undefined ? undefined : scaleChartValue(row.current, newSeriesDivisor),
        }))
      : newCrremRows.emission;
    return (
      scaled?.map((row: CrremRow) => ({
        ...row,
        baseline: row.current,
        baselinePrimary: getPrimaryEnergy(epcInfo, scaled, row),
        current: row.climate,
      })) ?? []
    );
  }, [epcInfo, newCrremRows.emission, newSeriesDivisor]);

  var dataInit = crremData || [];

  const dataRowsEqual = (left: DataRow[], right: DataRow[]) => {
    if (left === right) return true;
    if (left.length !== right.length) return false;
    return left.every((row, index) => {
      const other = right[index];
      return (
        row.year === other.year &&
        row.crrem === other.crrem &&
        row.current === other.current &&
        row.modernized === other.modernized &&
        row.climate === other.climate &&
        row.baseline === other.baseline
      );
    });
  };

  useEffect(() => {
    if (!hasModernization) return;
    const retrofitsWithAmount = selectedMeaures?.map((retrofit: SelectedMeasure) => {
      const correspondingRetrofit = measures?.find((measure: Measure) => measure.name === retrofit.name);
      if (correspondingRetrofit) {
        const amount =
          quantity !== "consumption"
            ? correspondingRetrofit?.[columnsAPINamesEnums.CARBON_DIOXIDE_SAVINGS]
            : correspondingRetrofit?.[columnsAPINamesEnums.REDUCTION_ENERGY_END];
        return {
          ...retrofit,
          amount: Number(amount),
          primaryModernized: convertValueBGFToNGF(
            correspondingRetrofit?.[columnsAPINamesEnums.REDUCTION_ENERGY_PRIMARY],
            BGF_NGF_Ratio
          ),
        };
      }
      return retrofit;
    });
    setRetrofits(retrofitsWithAmount);
  }, [JSON.stringify(selectedMeaures), quantity, crremScenario, measures, section]);

  useEffect(() => {
    if (!hasModernization) return;
    const selectedRetrofits = (retrofits || [])
      .map((retrofit: SelectedMeasure) => ({ ...retrofit, year: Number(retrofit.year) }))
      .filter((retrofit: SelectedMeasure) => retrofit.year >= RETROFITS_MIN_YEAR && retrofit.year <= 2130);
    const dataWithRetrofits = addRetrofitsToData(selectedRetrofits, dataInit);
    setData((prev) => (dataRowsEqual(prev, dataWithRetrofits) ? prev : dataWithRetrofits));
    if (quantity === "emission") {
      setDataEmission((prev) => (dataRowsEqual(prev, dataWithRetrofits) ? prev : dataWithRetrofits));
      return;
    }
    if (newCrremRows.hasData && newEmissionCrremData.length) {
      const emissionRetrofits = selectedMeaures?.map((retrofit: SelectedMeasure) => {
        const correspondingRetrofit = measures?.find((measure: Measure) => measure.name === retrofit.name);
        if (!correspondingRetrofit) return retrofit;
        return {
          ...retrofit,
          amount: Number(correspondingRetrofit?.[columnsAPINamesEnums.CARBON_DIOXIDE_SAVINGS]),
          primaryModernized: convertValueBGFToNGF(
            correspondingRetrofit?.[columnsAPINamesEnums.REDUCTION_ENERGY_PRIMARY],
            BGF_NGF_Ratio
          ),
          year: Number(retrofit.year),
        };
      });
      const nextEmissionData = addRetrofitsToData(emissionRetrofits, newEmissionCrremData);
      setDataEmission((prev) => (dataRowsEqual(prev, nextEmissionData) ? prev : nextEmissionData));
    }
  }, [
    BGF_NGF_Ratio,
    crremData,
    crremDataRaw,
    crremScenario,
    measures,
    newCrremRows.hasData,
    newEmissionCrremData,
    quantity,
    retrofits,
    selectedMeaures,
  ]);

  const dataYearRange: [number, number] = useMemo(() => {
    const years = data.map((row: DataRow) => row.year).filter(isNumericGuard);
    if (!years.length) return [MODERNIZATION_MIN_YEAR, MODERNIZATION_MAX_YEAR];

    const dataMin = Math.min(...years);
    const dataMax = Math.max(...years);
    const yearRangeMin = Math.max(dataMin, MODERNIZATION_MIN_YEAR);
    const yearRangeMax = Math.min(dataMax, MODERNIZATION_MAX_YEAR);
    return [yearRangeMin, yearRangeMax];
  }, [data]);

  useEffect(() => {
    setYearRange((prev) =>
      prev[0] === dataYearRange[0] && prev[1] === dataYearRange[1] ? prev : dataYearRange
    );
  }, [dataYearRange]);

  return (
    <WizardContext.Provider
      value={{
        crremData,
        crremScenario,
        data,
        dataEmission,
        dataYearRange,
        epcInfo,
        epcIsLoading,
        isAllmeasuresLoading,
        isSelectedMesuresLoading,
        measures: (measures ?? []) as Measure[],
        quantity,
        section,
        selectedMeaures: (!hasModernization ? [] : selectedMeaures ?? []) as SelectedMeasure[],
        setCrremScenario,
        setData,
        setQuantity,
        setSection,
        setYearRange,
        yearRange,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};
